import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Space, Typography, Row, Col, Skeleton, Switch } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { toast } from "../../components/toasts/toastStore";
import { usePermisos } from "../../context/PermisosContext";
import { apiServiciosInstance, authHeaderServicios } from "../../redux/actions/serviciosPrecios/serviciosPrecios";

import "./PacientesPage.css";
import "./ServiciosPreciosPage.css";

const { Title, Text, Paragraph } = Typography;

const BANNER_BG = "linear-gradient(135deg, var(--brand-dark, #3d5590) 0%, var(--brand-primary) 100%)";

const moneyServicio =(n) =>
  n == null || n === "" ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ServiciosPreciosPage() {
  const navigate = useNavigate();
  const { perm } = usePermisos() || { perm: () => true };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [incluirInactivos, setIncluirInactivos] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchServicios = useCallback(() => {
    setLoading(true);
    apiServiciosInstance
      .get("/servicios-precios", {
        headers: authHeaderServicios(),
        params: {
          search: searchDebounced || undefined,
          incluir_inactivos: incluirInactivos || undefined,
        },
      })
      .then((res) => setItems(Array.isArray(res.data) ? res.data : res.data?.items ?? []))
      .catch(() => toast.error("No se pudieron cargar los servicios"))
      .finally(() => setLoading(false));
  }, [searchDebounced, incluirInactivos]);

  useEffect(() => {
    fetchServicios();
  }, [fetchServicios]);

  return (
    <main className="paq-main">
      <div className="paq-content">
        <section className="paq-section">
          <div className="paq-filters-panel">
            <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
              <Title level={2} className="paq-title">Servicios y precios</Title>
              <Text className="paq-subtitle">Catálogo de servicios oftalmológicos y su precio de lista.</Text>
            </Space>

            <Row gutter={[16, 14]}>
              <Col xs={24} lg={18}>
                <div className="paq-field-label">Buscador</div>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar servicio..."
                  suffix={<SearchOutlined className="paq-input-suffix" />}
                  className="paq-control"
                  allowClear
                />
              </Col>
              <Col xs={24} lg={6}>
                <div className="paq-field-label">Inactivos</div>
                <label className="svp-toggle-inactivos">
                  <Switch size="small" checked={incluirInactivos} onChange={setIncluirInactivos} />
                  Mostrar inactivos
                </label>
              </Col>
            </Row>

            <Row gutter={[16, 14]} style={{ marginTop: 14 }} align="bottom">
              <Col xs={24} lg={6}>
                <div className="paq-actions">
                  <Button
                    className="paq-btn-clean"
                    onClick={() => {
                      setSearch("");
                      setIncluirInactivos(false);
                    }}
                  >
                    Limpiar filtros
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          <div className="paq-expedientes-card">
            <div className="paq-toolbar">
              <div className="paq-toolbar-left">
                <Title level={4} style={{ marginBottom: 0 }}>Servicios ({items.length})</Title>
                <Text type="secondary">{items.length} servicios encontrados</Text>
              </div>
              <div className="paq-toolbar-right">
                {perm("servicios_precios", "insertar") && (
                  <Button
                    type="primary"
                    icon={<i className="fa-solid fa-plus" />}
                    onClick={() => navigate("/app/servicios/crear")}
                    style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
                  >
                    Nuevo servicio
                  </Button>
                )}
              </div>
            </div>

            {!loading && items.length === 0 && (
              <div className="paq-empty">
                <div style={{ fontSize: 16, fontWeight: 500 }}>Sin servicios que coincidan con los filtros</div>
              </div>
            )}

            <div className="paq-grid">
              {loading &&
                Array.from({ length: 8 }).map((_, i) => (
                  <div key={`sk-${i}`} className="paq-card paq-card-skeleton">
                    <div className="paq-card-header">
                      <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
                    </div>
                    <div className="paq-card-body">
                      <Skeleton active title={false} paragraph={{ rows: 2 }} />
                    </div>
                  </div>
                ))}

              {!loading &&
                items.map((s) => {
                  const activo = s.active === undefined ? true : Boolean(s.active);
                  return (
                    <div key={s.id_servicio} className="paq-card">
                      <div className="paq-card-banner" style={{ background: BANNER_BG }}>
                        <i className="fa-solid fa-tags paq-card-banner-icon" />
                      </div>

                      <div className="paq-card-header">
                        <div className="paq-card-head">
                          <Paragraph className="paq-card-name" ellipsis={{ rows: 2 }}>{s.nombre}</Paragraph>
                          <span className={`paq-tipo-badge ${activo ? "paq-tipo-activo" : "paq-tipo-inactivo"}`}>
                            {activo ? "Activo" : "Inactivo"}
                          </span>
                        </div>
                      </div>

                      <div className="paq-card-body">
                        <div className="paq-contenidos-box">
                          <i className="fa-solid fa-dollar-sign paq-info-icon" />
                          <div className="paq-info-text">
                            <span className="paq-info-label">Precio de lista</span>
                            <span className="paq-info-value">{moneyServicio(s.precio_lista)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="paq-card-footer">
                        <Button
                          className="paq-btn-details"
                          icon={<i className="fa-solid fa-circle-info" />}
                          onClick={() => navigate(`/app/servicios/${s.id_servicio}`)}
                        >
                          Ver detalles
                        </Button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
